export const meta = {
  id: '21-commands',
  order: 21,
  titleFi: 'Käskyt',
  titleEn: 'Commands',
  duolingoUnit: 'duo-24-commands',
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: ['trēsy', 'riña', 'dāria', 'blēnon', 'ipradagon', 'keligon', 'rijagon', 'ours:mīkeliemagon', 'ours:rūnikagon', 'ours:qrīdrūdir', 'ours:qrīdrolilla', 'ours:ēdrȳnty', 'ours:nāpradagon', 'ours:āvestragon', 'ours:hubītsos', 'ours:sōpikagon', 'ours:rūniaptys', 'ours:majorrāelagon', 'ours:hontītsos', 'ours:hakōbagon', 'ours:ōhokagon', 'ours:ūbnoqitta', 'ours:nārijuragon', 'ours:nākostōby', 'ours:rōvemagon', 'ours:nājesuragon', 'ours:rhūqūbagon', 'ours:vāedrītsos', 'ours:pasvordi', 'ours:kōdi', 'ours:viliñemagon'],
  exampleIds: ['got-s3e04-004', 'got-s7e02-002', 'hotd-s1e07-006', 'got-s4e06-003', 'hotd-s1e06-004', 'got-s5e09-019', 'got-s3e10-004', 'ex-6ee57691711b', 'ex-a77406fb0e29', 'ex-c936c0380b9b', 'ex-322d35e66806', 'got-s3e10-005'],
  checkFocus: { mood: ['imperative'], cases: ['voc'] },   // what the end-of-lesson check tests first
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Commands',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Vocative',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Imperative',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Verb_Moods#Special_expressions',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['imperative'],
  grammarAlso: ['cases', 'negation'],
  drills: { cases: ['voc'], mood: ['imperative'] },
  showLines: ['got-s3e04-004', 'got-s7e02-002', 'hotd-s1e07-006', 'got-s4e06-003', 'hotd-s1e06-004', 'got-s5e09-019', 'got-s3e10-004', 'got-s3e10-005'],
  pronunciation: { focus: 'command-stress', lines: ['got-s3e04-004', 'got-s7e02-002', 'got-s4e06-003'] },
  letter: {
    fi: 'Anna kumppanillesi kolme lempeää käskyä ja puhuttele häntä vokatiivissa.',
    en: 'Give your partner three gentle commands, addressing them in the vocative.',
  },
  scene: 'got-s3e10-1',
}

# Käskyt

Jos olet katsonut sarjoja tarkkaan, olet kuullut valyriaa ehkä useimmin käskyinä: Daenerys komentaa Tahrattomia, ja lohikäärmeratsastajat huutavat lohikäärmeilleen <Hv>dohaerās!</Hv> ja <Hv>sōvēs!</Hv>. Tällä oppitunnilla opit kaksi asiaa, jotka kuuluvat yhteen: **vokatiivin**, jolla puhutellaan, ja **käskyt** – myönteiset ja kielteiset, puhuteltavalle ja muille.

Aloitetaan kohtauksesta, jossa kumpikin kuuluu neljässä sanassa:

<Line id="got-s3e04-004" />

## Vokatiivi: puhuttelun sija

Vokatiivi on sija, jolla puhutellaan suoraan: *Hei, poika!* – <Hv glossed>Rytsas, taobus!</Hv>

Suomessa ei ole vokatiivia. Puhuttelemme nominatiivilla (*Poika! Tyttö! Kuningatar!*), joten suomesta ei ole tässä apua. Hyvä uutinen on, että valyrian vokatiivi on helppo tunnistaa: Duolingon Tips & Notesin mukaan **siinä on aina <Hv>s</Hv>**.

Yksikössä ja monikossa *s* on aina mukana. <Term id="muutama">Muutamassa</Term> vokatiivi on sama kuin <Term id="instrumentaali">instrumentaali</Term> (<Hv>valussa</Hv>), ja <Term id="kollektiivi">kollektiivissa</Term> *s*:n tilalla on *z* (<Hv l="vala">valarza</Hv>).

Useimmissa suvun ja luvun yhdistelmissä vokatiivi syntyy, kun nominatiiviin lisätään <Hv>-s</Hv>:

- <Hv l="āeksio">āeksio</Hv> → <Hv glossed>Āeksios!</Hv> ’Isäntä!’
- <Hv>zaldrīzesse</Hv> → <Hv glossed>Zaldrīzesses!</Hv> ’Lohikäärmeet!’
- <Hv>vali</Hv> → <Hv glossed>Valis!</Hv> ’Miehet!’
- <Hv l="dāria">dārī</Hv> → <Hv glossed>Dārīs!</Hv> ’Kuningattaret!’
- <Hv>trēsy</Hv> → <Hv glossed>Trēsys!</Hv> ’Poika!’ (jonkun poika)

Aurinkosuvussa yksikön vokatiivi on usein **sama kuin nominatiivi**, koska siinä on jo <Hv>s</Hv>. Esimerkiksi <Hv glossed>azantys</Hv> ’ritari’ on vokatiivissakin <Hv>azantys</Hv>, ja Daenerysin ystävällinen puhuttelu <Hv glossed>ñuhys raqiros</Hv> ’ystäväni’ (repliikki kielteisen käskyn kohdalla) näyttää samalta kuin nominatiivi.

Joissakin yhdistelmissä vokaali myös vaihtuu ja joskus pitenee:

- <Hv l="riña">riña</Hv> → <Hv glossed>Riñus!</Hv> ’Tyttö!’
- <Hv>zaldrīzes</Hv> → <Hv glossed>Zaldrīzys!</Hv> ’Lohikäärme!’
- <Hv l="dāria">dāria</Hv> → <Hv glossed>Dārȳs!</Hv> ’Kuningatar!’
- <Hv l="āeksio">āeksia</Hv> → <Hv glossed>Āeksīs!</Hv> ’Isännät!’
- <Hv>anne</Hv> → <Hv glossed>Annys!</Hv> ’Hevonen!’

Melisandre tervehtii kuningatarta juuri näin:

<Line id="got-s7e02-002" />

**Maa- ja vesisuvussa** on oltava tarkkana: suvulle tunnusomainen <Hv>n</Hv> tai <Hv>r</Hv> katoaa vokatiivissa.

- <Hv>blēnon</Hv> → <Hv glossed>Blēnos!</Hv> ’Vuori!’
- <Hv>lōgor</Hv> → <Hv glossed>Lōgos!</Hv> ’Laiva!’

Sama näkyy lohikäärmeen nimessä. <Hv glossed>Vagar</Hv> eli Vhagar on vesisukuinen nimi, joten Aemond puhuttelee sitä muodolla <Hv l="vagar">Vagus</Hv>:

<Line id="hotd-s1e07-006" />

<Paradigm lemma="riña" cases="nom,voc" numbers="sg,pl" />

<Paradigm lemma="dāria" cases="nom,voc" numbers="sg,pl" />

<Paradigm lemma="blēnon" cases="nom,voc" numbers="sg,pl" />

<Pronounce word="Dārȳs" />

## Käsky sinulle ja teille

Valyriassa käskyn muoto riippuu kolmesta asiasta: käsketäänkö puhuteltavaa (2. persoona) vai jotakuta muuta, onko käskettäviä yksi vai useampi ja onko käsky myönteinen vai kielteinen. Aloitetaan perusmuodosta: myönteinen käsky 2. persoonalle.

Toisen persoonan imperatiivissa on eri muoto yksikölle ja monikolle – aivan kuten suomessa (*syö* ~ *syökää*). Muoto riippuu lisäksi siitä, päättyykö verbin vartalo konsonanttiin vai vokaaliin:

- **konsonanttivartalo, yksikkö**: <Hv glossed>Ipradās!</Hv> ’Syö!’
- **konsonanttivartalo, monikko**: <Hv glossed>Ipradātās!</Hv> ’Syökää!’
- **vokaalivartalo, yksikkö**: <Hv glossed>Kelīs!</Hv> ’Pysähdy!’
- **vokaalivartalo, monikko**: <Hv glossed>Kelītīs!</Hv> ’Pysähtykää!’

Konsonanttivartaloissa päätteet ovat siis <Hv>-ās</Hv> ja <Hv>-ātās</Hv>. Vokaalivartaloissa <Hv>ā</Hv>:n paikalle tulee vartalon oman loppuvokaalin pitkä muoto: <Hv>keligon</Hv> → <Hv>kelīs</Hv>, <Hv>sōvegon</Hv> → <Hv>sōvēs</Hv>.

<Note>
**Muistisääntö:** monikon käskyssä vartalon vokaali kuuluu kahdesti, ja molemmat ovat pitkiä: <Hv>kelītīs</Hv>, <Hv>sōvētēs</Hv>, <Hv>ipradātās</Hv>. Damien Halme (Academy of High Valyrian) vertaa tätä lastenlauluun, jossa jokainen vokaali vaihdetaan samaksi. Yleisin virhe on unohtaa ensimmäinen pituusviiva (*kelitīs*).
</Note>

<Compare fi="syö|kää" hv="iprad|ātās" label="imperatiivi, monikon 2. persoona" />

<Paradigm lemma="ipradagon" filter="imperative.active.present" numbers="sg,pl" />

<Paradigm lemma="keligon" filter="imperative.active.present" numbers="sg,pl" />

Sarjoissa tätä kuulee jatkuvasti. Luke käskee Arraxia pysähtymään – yksi lohikäärme, siis yksikkö:

<Line id="hotd-s1e06-004" />

Daenerys käskee lohikäärmettä lentämään. Verbi <Hv>sōvegon</Hv> on <Hv>e</Hv>-vartaloinen, joten yksikkö on <Hv>Sōvēs!</Hv> ja monikko <Hv>Sōvētēs!</Hv>.

<Line id="got-s5e09-019" />

Kolmannen tuotantokauden lopussa hänellä on kolme lohikäärmettä:

<Line id="got-s3e10-004" />

<Interlinear hv="Kelīs!" gloss="pysähtyä-IMP.2SG" fi="Pysähdy!" />

<Pronounce word="ipradagon" />

## Kielteinen käsky: infinitiivi + daor

Kielteinen käsky on yllättävän yksinkertainen: **<Term id="infinitiivi">infinitiivi</Term> ja perään <Hv>daor</Hv>**.

- <Hv glossed>Ipradagon daor!</Hv> ’Älä syö!’
- <Hv glossed>Keligon daor!</Hv> ’Älä pysähdy!’

Suomessa kielteinen käsky tarvitsee oman kieltoverbinsä, ja siinä yksikkö ja monikko erotetaan (*älä syö* ~ *älkää syökö*). Valyrian infinitiivi ei taivu lainkaan, joten sama muoto kelpaa yhdelle ja monelle. Wikin tapaluokkasivun mukaan syy on se, ettei imperatiivia voi yhdistää sanaan <Hv>daor</Hv>; sen sijasta käytetään kielteistä infinitiiviä.

<Compare glossed fi="äl|ä syö" hv="iprad|agon daor" label="kielteinen käsky ~ infinitiivi + daor" />

<Line id="got-s4e06-003" />

<Example id="ex-6ee57691711b" />

Jälkimmäisessä puhutellaan useampaa kuulijaa (*älkää naurako*), mutta valyrian infinitiivi on silti sama.

## Käsky jollekulle muulle: vokatiivi + infinitiivi

Käskyn voi antaa myös jollekulle muulle kuin puhuteltavalle. Englannissa nämä käännetään usein *let*-rakenteella, vaikka kyse ei olisi luvan pyytämisestä: kun esimies sanoo ”anna kun katson, mitä olet tehnyt”, hän ei oikeasti pyydä lupaa.

Valyriassa tällainen käsky muodostetaan **infinitiivillä ja käskyn saajan vokatiivilla**. Vokatiivi tulee suoraan verbin edelle:

- <Hv glossed>Nykys ipradagon!</Hv> ’Minä syön! / Anna kun syön!’
- <Hv glossed>Taobus keligon!</Hv> ’Pysähtyköön poika!’
- <Hv glossed>Azanti ābrus rijagon!</Hv> ’Kehukoon nainen ritaria!’

<Interlinear hv="Azant|i ābr|us rij|agon!" gloss="ritari-ACC nainen-VOC kehua-INF" fi="Kehukoon nainen ritaria!" />

Tässä suomesta on apua: meillä on 3. persoonan imperatiivi (*kehukoon*, *syököön*, *syökööt*), joka hoitaa täsmälleen saman tehtävän. Ero on vain siinä, että suomessa verbi taipuu, kun taas valyriassa verbi jää infinitiiviin ja käskyn saaja merkitään vokatiivilla. Minä-muodolle (<Hv glossed>Nykys ipradagon!</Hv>) ei suomessa ole vastinetta; lähimmäs pääsee *anna kun syön* tai *minä syön nyt*.

<Compare hvFi="kehukoon nainen" fi="kehu|koon" hv="ābr|us rijagon" label="3. persoonan imperatiivi ~ vokatiivi + infinitiivi" />

<Example id="ex-a77406fb0e29" />

<Example id="ex-c936c0380b9b" />

Pronominillakin on vokatiivi – tässä <Hv>zirys</Hv>, sanasta <Hv>ziry</Hv>:

<Example id="ex-322d35e66806" />

**Kielteinen** muoto saadaan jälleen lisäämällä loppuun <Hv>daor</Hv>:

- <Hv glossed>Zaldrīzesses ipradagon daor!</Hv> ’Älkööt lohikäärmeet syökö!’
- <Hv glossed>Taobus keligon daor!</Hv> ’Älköön poika pysähtykö!’

<Note>
Duolingon ohjeet huomauttavat, että valyria erottaa **käskyn** ja **luvan pyytämisen**, vaikka englannin *let* hämärtää eron. Wikin tapaluokkasivun mukaan lupaa pyydetään datiivilla ja infinitiivillä. Daenerys ei käske itseään vaan pyytää päästä ohi, ja siksi lauseessa on datiivi <Hv glossed>ynot</Hv> ’minulle’ eikä vokatiivi <Hv>nykys</Hv>:

<Line id="got-s3e10-005" />

Saman sivun mukaan vokatiivi + infinitiivi + <Hv glossed>daor</Hv> voi olla kaksiselitteinen: se voi tarkoittaa sekä ’älköön X tehkö’ että ’X, älä tee’.
</Note>

<Note>
Kehotus ’tehdään’ on vielä helpompi: <Hv glossed>īlot</Hv> ’meille’ ja perään infinitiivi. Damien Halme (Academy of High Valyrian) opettaa sen heti käskyjen yhteydessä. Rakenteeseen palataan tarkemmin <LessonLink n={42}>oppitunnilla 42</LessonLink>.

<Practice hv="Īlot ipradagon!" fi="Syödään!" />

<Practice hv="Īlot dakogon!" fi="Juostaan!" />
</Note>

<Note>
**Muotojen kuuntelukortit.** Tämän oppitunnin jälkeen päivän korteissa voi tulla vastaan myös tuttujen verbien käskyjä ja tuttujen sanojen vokatiiveja kuuntelukortteina. Sano, mitä sana tarkoittaa ja mikä muoto se on, esimerkiksi ”käsky sinulle”.
</Note>

## Harjoituslauseita

Nämä lauseet on koottu tämän oppitunnin säännöillä ja taivutustaulukoiden muodoilla. Ne eivät ole Petersonin lauseita.

<Practice hv="Riñus, havon ipradās!" fi="Tyttö, syö leipää!" />

<Practice hv="Rijātās!" fi="Kehukaa!" />

<Practice hv="Blēnot jagon daor!" fi="Älä mene vuorelle!" />

<Practice hv="Kerille valis ipradagon!" fi="Syökööt miehet keittoa!" />

<Practice hv="Trēsys keligon!" fi="Pysähtyköön poika!" />

Neljännessä lauseessa vokatiivi <Hv>valis</Hv> on suoraan verbin edellä ja objekti <Hv>kerille</Hv> vokatiivin edellä – sama järjestys kuin lauseessa <Hv fi="Kehukoon nainen ritaria">Azanti ābrus rijagon</Hv>.

## Sanat

<Vocab />

<Check />
